import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaDollarSign, FaClipboardList, FaFileAlt, FaArrowLeft, FaSpinner, FaSave } from 'react-icons/fa';
import { createService, updateService, getServiceById } from './serviceService';
import DashboardLayout from '../../layouts/DashboardLayout';
import Input from '../../components/shared/Input';
import TextArea from '../../components/shared/TextArea';
import MaskedInput from '../../components/shared/MaskedInput';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import {
  FormContainer,
  FormSection,
  FormHeader,
  BackButton,
  FormGrid,
  FormField,
  SubmitButton,
} from '../../components/styled/FormComponents';

export default function ServiceForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(id ? true : false);
  const [submitting, setSubmitting] = useState(false);
  const [service, setService] = useState({
    service_name: '',
    price_per_sqft: '',
    description: '',
    template: '',
  });

  useEffect(() => {
    const fetchService = async () => {
      if (id) {
        try {
          const response = await getServiceById(id);
          setService(response.data);
        } catch (error) {
          console.error('Error fetching service:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchService();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (id) {
        await updateService(id, service);
      } else {
        await createService(service);
      }
      navigate('/services');
    } catch (error) {
      console.error('Error saving service:', error);
      // Here you might want to add error handling/display
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <FormContainer>
          <LoadingSpinner />
        </FormContainer>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <FormContainer>
        <FormHeader>
          <BackButton onClick={() => navigate('/services')}>
            <FaArrowLeft /> Back to Services
          </BackButton>
          <h2>{id ? 'Edit Service' : 'New Service'}</h2>
        </FormHeader>

        <form onSubmit={handleSubmit}>
          <FormSection>
            <h3>Basic Information</h3>
            <FormGrid>
              <FormField index={0}>
                <Input
                  label="Service Name"
                  icon={<FaClipboardList />}
                  value={service.service_name}
                  onChange={(e) => setService({ ...service, service_name: e.target.value })}
                  placeholder="Enter service name"
                  required
                />
              </FormField>

              <FormField index={1}>
                <MaskedInput
                  label="Price per Sq Ft"
                  icon={<FaDollarSign />}
                  mask="currency"
                  value={service.price_per_sqft}
                  onChange={(e) => setService({ ...service, price_per_sqft: e.target.value })}
                  placeholder="Enter price per sq ft"
                  required
                />
              </FormField>
            </FormGrid>
          </FormSection>

          <FormSection>
            <h3>Service Details</h3>
            <FormField index={2}>
              <TextArea
                label="Description"
                icon={<FaFileAlt />}
                value={service.description}
                onChange={(e) => setService({ ...service, description: e.target.value })}
                placeholder="Enter a detailed description of the service"
                rows={4}
                required
              />
            </FormField>

            <FormField index={3}>
              <TextArea
                label="Template"
                icon={<FaFileAlt />}
                value={service.template}
                onChange={(e) => setService({ ...service, template: e.target.value })}
                placeholder="Enter the service template content"
                rows={6}
                helperText="This template will be used to populate service-related fields in other forms"
              />
            </FormField>
          </FormSection>

          <SubmitButton type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <FaSpinner className="icon-spin" />
                {id ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <FaSave />
                {id ? 'Update Service' : 'Create Service'}
              </>
            )}
          </SubmitButton>
        </form>
      </FormContainer>
    </DashboardLayout>
  );
}